import { cn } from "@win-reality/react-ui";

import { BaserunnerFlags } from "../utils/pitchTagging";
import { BaseRunners } from "./BaseRunners";
import { InningHalfIcon } from "./InningHalfIcon";

const CountIndicators = ({
  label,
  count,
  max,
  "data-testid": testId,
}: {
  label: string;
  count: number;
  max: number;
  "data-testid"?: string;
}) => (
  <div
    className="flex items-center gap-1.5"
    data-testid={testId}
    data-value={count}
  >
    <span className="text-sm font-normal text-gray-900 dark:text-white">
      {label}
    </span>
    <div className="flex gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-2.5 w-2.5 rounded-full",
            i < count ? "dark:bg-white bg-black" : "bg-transparent border",
          )}
          style={
            i >= count
              ? {
                  borderWidth: "1.5px",
                  borderColor: "var(--tagging-count-indicator-border)",
                }
              : undefined
          }
        />
      ))}
    </div>
  </div>
);

type ScoreboardProps = {
  awayTeamName: string;
  awayTeamColor: string;
  awayScore: number;
  homeTeamName: string;
  homeTeamColor: string;
  homeScore: number;
  baserunners: BaserunnerFlags;
  inning: number;
  isTopInning: boolean;
  balls: number;
  strikes: number;
  outs: number;
  onEditClick?: () => void;
};

export const Scoreboard = ({
  awayTeamName,
  awayTeamColor,
  awayScore,
  homeTeamName,
  homeTeamColor,
  homeScore,
  baserunners,
  inning,
  isTopInning,
  balls,
  strikes,
  outs,
  onEditClick,
}: ScoreboardProps) => {
  return (
    <div
      className="space-y-2 bg-gray-100 p-2 dark:bg-[#2B2C31]"
      style={{ borderRadius: "13px" }}
      data-testid="scoreboard-panel"
    >
      {/* Teams and Scores */}
      <div className="mb-3 flex items-center justify-center gap-5">
        {/* Away Team */}
        <div className="flex flex-col items-center gap-0.5">
          <div
            style={{ color: awayTeamColor }}
            className="text-base font-bold"
            data-testid="scoreboard-team-value"
            data-team="away"
            data-value={awayTeamName}
          >
            {awayTeamName}
          </div>
          <div
            className="text-2xl font-bold text-gray-900 dark:text-white"
            data-testid="scoreboard-score-value"
            data-team="away"
            data-value={awayScore}
          >
            {awayScore}
          </div>
        </div>

        {/* Baserunners Diamond. The top padding seats the bases around the
            home plate marker below */}
        <div
          className="relative flex justify-center pt-2"
          style={{ height: "80px" }}
        >
          <BaseRunners
            baserunners={baserunners}
            editable={false}
            data-testid="scoreboard-base-runners-panel"
          />

          {/* Home Plate (center) */}
          <div
            className="absolute left-1/2 flex -translate-x-1/2 items-center gap-1"
            style={{ top: "77%" }}
          >
            <InningHalfIcon isTopInning={isTopInning} />
            <div
              className="text-base font-bold text-gray-900 dark:text-white"
              data-testid="scoreboard-inning-value"
              data-value={inning}
              data-half={isTopInning ? "top" : "bottom"}
            >
              {inning}
            </div>
          </div>
        </div>

        {/* Home Team */}
        <div className="flex flex-col items-center gap-0.5">
          <div
            style={{ color: homeTeamColor }}
            className="text-base font-bold"
            data-testid="scoreboard-team-value"
            data-team="home"
            data-value={homeTeamName}
          >
            {homeTeamName}
          </div>
          <div
            className="text-2xl font-bold text-gray-900 dark:text-white"
            data-testid="scoreboard-score-value"
            data-team="home"
            data-value={homeScore}
          >
            {homeScore}
          </div>
        </div>
      </div>

      {/* Count Indicators: Balls, Strikes, Outs */}
      <div className="flex items-center justify-center gap-4">
        <CountIndicators
          label="B"
          count={balls}
          max={4}
          data-testid="scoreboard-balls-value"
        />
        <CountIndicators
          label="S"
          count={strikes}
          max={3}
          data-testid="scoreboard-strikes-value"
        />
        <CountIndicators
          label="O"
          count={outs}
          max={3}
          data-testid="scoreboard-outs-value"
        />
      </div>

      {/* Edit Link */}
      <button
        onClick={onEditClick}
        data-testid="scoreboard-edit-game-state-button"
        className={cn(
          "w-full text-center text-xs text-gray-900 underline dark:text-white",
          "hover:no-underline focus:outline-none",
        )}
      >
        Edit Live Game State
      </button>
    </div>
  );
};
